import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle, ChevronRight, ChevronLeft, Loader2, Download } from 'lucide-react';
import SignatureCanvas from '../components/SignatureCanvas';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `${window.location.origin}/pdf.worker.min.mjs`;

// Log worker configuration for debugging
console.log('PDF.js Worker configured:', pdfjs.GlobalWorkerOptions.workerSrc);
console.log('PDF.js Version:', pdfjs.version);

// Date formatting utility functions
const formatDateForDisplay = (isoDate, format) => {
  if (!isoDate) return '';
  const date = new Date(isoDate + 'T00:00:00'); // Ensure local timezone
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  // Normalize format to uppercase for case-insensitive matching
  const normalizedFormat = format ? format.toUpperCase() : 'YYYY-MM-DD';

  switch (normalizedFormat) {
    case 'DD/MM/YYYY':
      return `${day}/${month}/${year}`;
    case 'MM/DD/YYYY':
      return `${month}/${day}/${year}`;
    case 'YYYY/MM/DD':
      return `${year}/${month}/${day}`;
    case 'DD-MM-YYYY':
      return `${day}-${month}-${year}`;
    case 'MM-DD-YYYY':
      return `${month}-${day}-${year}`;
    case 'YYYY-MM-DD':
    default:
      return isoDate; // Already in YYYY-MM-DD format
  }
};

const parseDateFromFormat = (dateStr, format) => {
  if (!dateStr) return '';

  let year, month, day;
  const parts = dateStr.split(/[-/]/);

  switch (format) {
    case 'DD/MM/YYYY':
    case 'DD-MM-YYYY':
      [day, month, year] = parts;
      break;
    case 'MM/DD/YYYY':
    case 'MM-DD-YYYY':
      [month, day, year] = parts;
      break;
    case 'YYYY/MM/DD':
    case 'YYYY-MM-DD':
    default:
      [year, month, day] = parts;
      break;
  }

  // Return ISO format (YYYY-MM-DD) for storage
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

// Type-aware check for whether a field actually has a real, user-set value.
// Centralizes "is this field filled/selected" so checkbox/checkbox-group
// (which use false/[] rather than '' to mean "empty") are handled consistently
// everywhere fieldValues is read (required-field validation, progress counter,
// review list visibility, sidebar completion indicator, etc.).
const isFieldValueSet = (field, value) => {
  const fieldType = field?.field_type || field?.type;
  if (fieldType === 'checkbox') {
    return value === true || value === 'true';
  }
  if (fieldType === 'checkbox-group') {
    return Array.isArray(value) ? value.length > 0 : Boolean(value);
  }
  return value !== '' && value !== null && value !== undefined;
};

// Helper function to generate initials from full name
const generateInitialsFromName = (fullName) => {
  if (!fullName || typeof fullName !== 'string') return '';

  const nameParts = fullName.trim().split(/\s+/);
  if (nameParts.length === 0) return '';

  // Get first letter of first name and first letter of last name
  if (nameParts.length === 1) {
    // Only one name part - use first letter
    return nameParts[0].charAt(0).toUpperCase();
  }

  // Multiple name parts - use first letter of first and last
  const firstInitial = nameParts[0].charAt(0).toUpperCase();
  const lastInitial = nameParts[nameParts.length - 1].charAt(0).toUpperCase();
  return firstInitial + lastInitial;
};

// Helper function to generate initials image (typed style)
const generateInitialsImage = (initials) => {
  if (!initials) return null;

  const canvas = document.createElement('canvas');
  // Very small canvas size for text-like initials
  canvas.width = 60;
  canvas.height = 24;
  const ctx = canvas.getContext('2d');

  // Clear canvas with white background
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // Set font and style - small, clean font like regular text
  ctx.font = '16px Arial, sans-serif';
  ctx.fillStyle = '#000000';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  // Draw initials
  ctx.fillText(initials, canvas.width / 2, canvas.height / 2);

  // Crop to content to remove excess whitespace
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const pixels = imageData.data;

  let minX = canvas.width, minY = canvas.height, maxX = 0, maxY = 0;

  // Find bounds of non-white pixels
  for (let y = 0; y < canvas.height; y++) {
    for (let x = 0; x < canvas.width; x++) {
      const i = (y * canvas.width + x) * 4;
      const r = pixels[i];
      const g = pixels[i + 1];
      const b = pixels[i + 2];

      // Check if pixel is not white (has content)
      if (r < 250 || g < 250 || b < 250) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }

  // Add minimal padding
  const padding = 5;
  minX = Math.max(0, minX - padding);
  minY = Math.max(0, minY - padding);
  maxX = Math.min(canvas.width, maxX + padding);
  maxY = Math.min(canvas.height, maxY + padding);

  // Create cropped canvas with white background
  const croppedWidth = maxX - minX;
  const croppedHeight = maxY - minY;

  if (croppedWidth > 0 && croppedHeight > 0) {
    const croppedCanvas = document.createElement('canvas');
    croppedCanvas.width = croppedWidth;
    croppedCanvas.height = croppedHeight;
    const croppedCtx = croppedCanvas.getContext('2d');

    // Fill with white background
    croppedCtx.fillStyle = '#ffffff';
    croppedCtx.fillRect(0, 0, croppedWidth, croppedHeight);

    // Draw the cropped content
    croppedCtx.drawImage(
      canvas,
      minX, minY, croppedWidth, croppedHeight,
      0, 0, croppedWidth, croppedHeight
    );

    return croppedCanvas.toDataURL('image/png');
  }

  // Return as data URL
  return canvas.toDataURL('image/png');
};

const SigningSession = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  
  const [isLoading, setIsLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [envelope, setEnvelope] = useState(null);
  const [fields, setFields] = useState([]);
  const [documentUrl, setDocumentUrl] = useState(null);
  const [currentFieldIndex, setCurrentFieldIndex] = useState(0);
  const [fieldValues, setFieldValues] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [isWaiting, setIsWaiting] = useState(false);
  const [waitingMessage, setWaitingMessage] = useState('');
  const [alreadySigned, setAlreadySigned] = useState(false);
  const [alreadySignedMessage, setAlreadySignedMessage] = useState('');
  const [sessionInvalid, setSessionInvalid] = useState(false);
  const [validationChecked, setValidationChecked] = useState(false);
  const [fieldScales, setFieldScales] = useState({}); // Store zoom/scale for each field
  const [showReviewPage, setShowReviewPage] = useState(false); // Show review page before submit
  const [reviewFieldIndex, setReviewFieldIndex] = useState(null); // Currently adjusting field in review
  const [pageNumber, setPageNumber] = useState(1); // Current page number
  const [numPages, setNumPages] = useState(null); // Total PDF pages
  const [pageWidth, setPageWidth] = useState(800); // Width for PDF rendering
  const pageHeight = pageWidth * (11 / 8.5); // Standard US Letter aspect ratio
  const [showSignatureModal, setShowSignatureModal] = useState(false); // Show signature modal
  const [activeFieldForSignature, setActiveFieldForSignature] = useState(null); // Field being signed
  const [editingFieldId, setEditingFieldId] = useState(null); // Field being edited inline
  const [signatureColor, setSignatureColor] = useState('#000000'); // Signature color
  const [textStyle, setTextStyle] = useState('normal'); // Text style for text fields
  const [showDeclineModal, setShowDeclineModal] = useState(false); // Show decline confirmation modal
  const [isDeclining, setIsDeclining] = useState(false); // Declining in progress
  const [isDeclined, setIsDeclined] = useState(false); // Document was declined
  const [isDownloading, setIsDownloading] = useState(false); // Download in progress
  const [isCCRecipient, setIsCCRecipient] = useState(false); // CC recipient (not a signer)
  const [ccMessage, setCCMessage] = useState(''); // Message for CC recipient
  const [textOverflowWarnings, setTextOverflowWarnings] = useState({}); // Track text overflow per field (maxLength exceeded)
  const containerRef = useRef(null);
  const fieldRefs = useRef({}); // Store refs for each field overlay
  const textMeasureRef = useRef(null); // Hidden element for measuring text width

  // Handle download signed document
  const handleDownloadSignedDocument = async () => {
    try {
      setIsDownloading(true);
      const response = await axios.get(`${API}/signing-sessions/${token}/download`, {
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${envelope?.title || 'document'}_signed.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Document downloaded successfully!');
    } catch (error) {
      console.error('Failed to download document:', error);
      toast.error('Failed to download document. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  // Handle PDF document load
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };
  
  // Scroll to exact field position
  const scrollToFieldPosition = (fieldIndex) => {
    if (!containerRef.current) return;
    
    const field = fields[fieldIndex];
    if (!field) return;
    
    // Get the field overlay element
    const fieldElement = fieldRefs.current[field.id];
    if (fieldElement) {
      // Scroll to the field with smooth behavior
      fieldElement.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center',
        inline: 'center'
      });
    }
  };

  // Auto-navigate to the page of current field (but don't auto-scroll)
  useEffect(() => {
    if (fields.length > 0 && currentFieldIndex < fields.length) {
      const field = fields[currentFieldIndex];
      if (field) {
        // Handle both field.page and field.rect.page formats
        const fieldPage = field.page || (field.rect && field.rect.page) || 1;
        setPageNumber(fieldPage);
      }
    }
  }, [currentFieldIndex, fields]);
  
  // IMMEDIATE validation on mount - check BEFORE loading anything else
  useEffect(() => {
    const quickValidation = async () => {
      try {
        // Quick status check - will fail with 403 if already signed
        await axios.get(`${API}/signing-sessions/${token}`);
        setValidationChecked(true);
      } catch (error) {
        if (error.response?.status === 403) {
          const errorMessage = error.response?.data?.detail || 'This document has already been signed';
          setAlreadySigned(true);
          setAlreadySignedMessage(errorMessage);
          setIsLoading(false);
          // Show immediate alert
          alert(errorMessage);
        } else if (error.response?.status === 404) {
          setIsLoading(false);
          setSessionInvalid(true);
        }
        setValidationChecked(true);
      }
    };
    
    if (token && !validationChecked) {
      quickValidation();
    }
  }, [token, validationChecked, API]);
  
  // Load signing session (only if validation passed)
  useEffect(() => {
    if (!validationChecked || alreadySigned) return;
    
    const loadSession = async () => {
      try {
        setIsLoading(true);
        
        // Get signing session details
        const sessionResponse = await axios.get(`${API}/signing-sessions/${token}`);
        const sessionData = sessionResponse.data;
        
        // Check if this is a CC recipient (not a signing authority)
        if (sessionData.is_cc_recipient) {
          setIsCCRecipient(true);
          setCCMessage(sessionData.message);
          setSession(sessionData);
          // Also load envelope details for display
          try {
            const envelopeResponse = await axios.get(`${API}/public/signing-sessions/${token}/envelope`);
            setEnvelope(envelopeResponse.data);
          } catch (e) {
            console.error('Failed to load envelope for CC recipient:', e);
          }
          setIsLoading(false);
          return;
        }

        // Check if we're waiting for previous signers
        if (sessionData.waiting) {
          setIsWaiting(true);
          setWaitingMessage(sessionData.message);
          setSession(sessionData);
          setIsLoading(false);
          return;
        }

        setSession(sessionData);
        
        // Get envelope and fields using public token-based endpoints
        const envelopeResponse = await axios.get(`${API}/public/signing-sessions/${token}/envelope`);
        setEnvelope(envelopeResponse.data);
        
        const fieldsResponse = await axios.get(`${API}/public/signing-sessions/${token}/fields`);
        console.log('📋 Fields received from backend:', fieldsResponse.data);

        // Backend already filters fields by assignedTo matching recipient email
        // No additional filtering needed here - use all fields returned by backend
        const recipientFields = fieldsResponse.data;
        
        // Parse JSON strings for rect and props/properties
        const parsedFields = recipientFields.map(field => {
          const parsedField = { ...field };
          
          // Parse rect if it's a string
          if (typeof field.rect === 'string') {
            try {
              parsedField.rect = JSON.parse(field.rect);
            } catch (e) {
              console.error('Failed to parse rect for field:', field.id, e);
              parsedField.rect = {};
            }
          }
          
          // Parse props if it's a string
          if (typeof field.props === 'string') {
            try {
              parsedField.props = JSON.parse(field.props);
            } catch (e) {
              console.error('Failed to parse props for field:', field.id, e);
              parsedField.props = {};
            }
          }
          
          // Parse properties if it's a string and normalize to props
          if (typeof field.properties === 'string') {
            try {
              parsedField.props = JSON.parse(field.properties);
              parsedField.properties = parsedField.props; // Keep both for compatibility
            } catch (e) {
              console.error('Failed to parse properties for field:', field.id, e);
              parsedField.props = {};
            }
          } else if (field.properties && !field.props) {
            // If properties exists as object but props doesn't, use it
            parsedField.props = field.properties;
          }
          
          return parsedField;
        });
        
        console.log('📋 Parsed fields for recipient:', parsedFields);

        // Debug: Log radio/checkbox fields specifically
        parsedFields.forEach((field, idx) => {
          const fieldType = field.field_type || field.type;
          if (fieldType === 'radio' || fieldType === 'checkbox-group') {
            console.log(`[SIGNING] Radio/Checkbox field ${idx}:`, {
              id: field.id,
              type: fieldType,
              rect: field.rect,
              props: field.props,
              'props.options': field.props?.options,
              properties: field.properties
            });
            // Log each option's position
            (field.props?.options || []).forEach((opt, optIdx) => {
              console.log(`  [SIGNING] Option ${optIdx}: x=${opt.x}, y=${opt.y}, value=${opt.value}, label=${opt.label}`);
            });
          }
        });

        setFields(parsedFields);

        // Initialize field values
        const initialValues = {};
        recipientFields.forEach(field => {
          const fieldType = field.field_type || field.type;
          const defaultValue = field.props?.defaultValue;
          const hasDefaultValue = defaultValue !== undefined && defaultValue !== null && defaultValue !== '';
          // Only trust defaultValue if its shape actually matches the field type.
          // Some legacy fields store a checkbox-group-style `[]` default on a
          // single `checkbox` field, which is truthy and breaks the checked/
          // completion logic below if used as-is.
          if (fieldType === 'checkbox') {
            initialValues[field.id] = hasDefaultValue && (defaultValue === true || defaultValue === 'true');
          } else if (fieldType === 'checkbox-group') {
            initialValues[field.id] = (hasDefaultValue && Array.isArray(defaultValue)) ? defaultValue : [];
          } else if (hasDefaultValue) {
            initialValues[field.id] = defaultValue;
          } else {
            initialValues[field.id] = '';
          }
        });

        // Auto-generate initials from recipient name for all initials fields
        // The backend returns: { session: { display_name, email, ... }, fields, waiting }
        // So display_name lives inside sessionData.session, not at sessionData root level
        console.log('🔍 Session data for initials generation:', {
          'sessionData.display_name (root - usually undefined)': sessionData.display_name,
          'sessionData.session?.display_name (correct path)': sessionData.session?.display_name,
          'sessionData.session?.email': sessionData.session?.email,
          session: sessionData.session,
        });

        // Try multiple possible locations for the recipient name
        const recipientName = sessionData.display_name ||
                             sessionData.recipient_name ||
                             sessionData.session?.display_name ||
                             sessionData.session?.recipient_name ||
                             sessionData.session?.email ||  // fallback: use email to derive initials
                             sessionData.email ||
                             '';

        if (recipientName) {
          const initials = generateInitialsFromName(recipientName);
          console.log(`🔤 Auto-generated initials "${initials}" from name "${recipientName}"`);

          if (initials) {
            const initialsImage = generateInitialsImage(initials);
            console.log(`🖼️ Generated initials image:`, initialsImage ? 'Success' : 'Failed');

            // Find all initials fields and pre-populate them
            let populatedCount = 0;
            parsedFields.forEach(field => {
              const fieldType = field.field_type || field.type;
              console.log(`🔍 Checking field ${field.id}: type=${fieldType}`);

              if (fieldType === 'initials') {
                initialValues[field.id] = initialsImage;
                populatedCount++;
                console.log(`✅ Pre-populated initials field ${field.id} with "${initials}"`);
              }
            });

            console.log(`📊 Total initials fields populated: ${populatedCount}`);
          } else {
            console.warn('⚠️ Failed to generate initials from name:', recipientName);
          }
        } else {
          console.warn('⚠️ No recipient name found in session data');
        }

        setFieldValues(initialValues);
        
        // Load document using public token-based endpoints
        const docsResponse = await axios.get(`${API}/public/signing-sessions/${token}/docs`);
        if (docsResponse.data && docsResponse.data.length > 0) {
          const fileId = docsResponse.data[0].file_id;
          try {
            const fileResponse = await axios.get(`${API}/public/signing-sessions/${token}/files/${fileId}`, {
              responseType: 'blob'
            });
            const blobUrl = URL.createObjectURL(fileResponse.data);
            setDocumentUrl(blobUrl);
          } catch (e) {
            console.error('Failed to load document:', e);
          }
        }
        
      } catch (error) {
        console.error('Failed to load signing session:', error);
        if (error.response?.status === 404) {
          setSessionInvalid(true);
        } else if (error.response?.status === 403) {
          // Already signed - show message instead of error toast
          const errorMessage = error.response?.data?.detail || 'This document has already been signed';
          setAlreadySigned(true);
          setAlreadySignedMessage(errorMessage);
        } else {
          toast.error('Failed to load signing session');
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    if (token && validationChecked && !alreadySigned) {
      loadSession();
    }
    
    // Cleanup blob URL
    return () => {
      if (documentUrl && documentUrl.startsWith('blob:')) {
        URL.revokeObjectURL(documentUrl);
      }
    };
  }, [token, API, validationChecked, alreadySigned]);
  
  // Calculate PDF width based on container
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth - 32; // Account for padding
        setPageWidth(Math.min(width, 800)); // Max 800px
      }
    };
    
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [showReviewPage]);
  
  // Navigate between fields
  const goToNextField = () => {
    if (currentFieldIndex < fields.length - 1) {
      const nextIndex = currentFieldIndex + 1;
      setCurrentFieldIndex(nextIndex);
      // Scroll to the next field position after a short delay
      setTimeout(() => scrollToFieldPosition(nextIndex), 300);
    }
  };
  
  const goToPreviousField = () => {
    if (currentFieldIndex > 0) {
      const prevIndex = currentFieldIndex - 1;
      setCurrentFieldIndex(prevIndex);
      // Scroll to the previous field position after a short delay
      setTimeout(() => scrollToFieldPosition(prevIndex), 300);
    }
  };
  
  // Update field value
  const updateFieldValue = (fieldId, value) => {
    setFieldValues(prev => ({
      ...prev,
      [fieldId]: value
    }));
  };
  
  // Update field scale/zoom
  const updateFieldScale = (fieldId, scale) => {
    setFieldScales(prev => ({
      ...prev,
      [fieldId]: scale
    }));
  };
  
  // Get field scale (default 1.0)
  const getFieldScale = (fieldId) => {
    return fieldScales[fieldId] || 1.0;
  };
  
  // Check if all required fields are filled
  const areAllRequiredFieldsFilled = () => {
    return fields.every(field => {
      if (!field.props?.required && !field.properties?.required) return true;
      const value = fieldValues[field.id];
      return isFieldValueSet(field, value);
    });
  };
  
  // Show review page after all fields filled
  const handleShowReview = () => {
    if (!areAllRequiredFieldsFilled()) {
      toast.error('Please complete all required fields');
      return;
    }
    setShowReviewPage(true);
  };
  
  // Go back to editing from review page
  const handleBackToEdit = () => {
    setShowReviewPage(false);
    setReviewFieldIndex(null);
  };
  
  // Submit signing session (from review page)
  const handleFinalSubmit = async () => {
    try {
      setIsSubmitting(true);
      
      // Submit field values and scales
      await axios.post(`${API}/signing-sessions/${token}/complete`, {
        field_values: fieldValues,
        field_scales: fieldScales
      });
      
      setIsCompleted(true);
      toast.success('Document signed successfully!');
      
    } catch (error) {
      console.error('Failed to submit:', error);
      toast.error('Failed to submit signature. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="animate-spin h-12 w-12 text-indigo-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading document...</p>
        </div>
      </div>
    );
  }
  
  if (sessionInvalid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md p-8 bg-white rounded-lg shadow-lg">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <svg className="h-10 w-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-3">
            Sorry, the link is either already used or is no longer valid.
          </h1>
          <p className="text-gray-600 text-sm">
            Please contact the sender of the signature request for further details.
          </p>
        </div>
      </div>
    );
  }

  if (isCompleted) {
    if (isDeclined) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center max-w-md p-8 bg-white rounded-lg shadow-lg">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <svg className="h-10 w-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              You have declined this document
            </h1>
            <p className="text-gray-600 mb-6">
              The sender has been notified of your decision.
            </p>
          </div>
        </div>
      );
    }
    
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md p-8 bg-white rounded-lg shadow-lg">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Document Signed Successfully!
          </h1>
          <p className="text-gray-600 mb-6">
            Thank you for signing. All parties will be notified.
          </p>

          {/* Download Signed Document Button */}
          <button
            onClick={handleDownloadSignedDocument}
            disabled={isDownloading}
            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
          >
            {isDownloading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                Downloading...
              </>
            ) : (
              <>
                <Download className="h-5 w-5" />
                Download Signed Document
              </>
            )}
          </button>

          <p className="text-sm text-gray-500 mt-4">
            A copy has also been sent to your email.
          </p>
        </div>
      </div>
    );
  }

  // Show already signed screen
  if (alreadySigned) {
    const isDeclinedMessage = alreadySignedMessage.includes('declined');
    
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md p-8 bg-white rounded-lg shadow-lg">
          {isDeclinedMessage ? (
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <svg className="h-10 w-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
          ) : (
            <CheckCircle className="h-16 w-16 text-blue-500 mx-auto mb-4" />
          )}
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {isDeclinedMessage ? 'You have declined this document' : 'Already Signed'}
          </h1>
          <p className="text-gray-600 mb-6">
            {isDeclinedMessage ? 'The sender has been notified of your decision.' : alreadySignedMessage}
          </p>
          {!isDeclinedMessage && (
            <p className="text-sm text-gray-500">
              This document has been completed. You cannot sign it again.
            </p>
          )}
        </div>
      </div>
    );
  }
  
  // Show waiting screen if it's not this recipient's turn
  if (isWaiting) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md p-8 bg-white rounded-lg shadow-lg">
          <div className="h-16 w-16 mx-auto mb-4 relative">
            <Loader2 className="animate-spin h-16 w-16 text-amber-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Waiting for Previous Signers
          </h1>
          <p className="text-gray-600 mb-4">
            {waitingMessage}
          </p>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
            <p className="text-sm text-amber-800">
              You&apos;ll receive an email notification when it&apos;s your turn to sign.
            </p>
          </div>
          <button
            onClick={() => window.location.reload()}
            className="mt-6 px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
          >
            Refresh Status
          </button>
        </div>
      </div>
    );
  }
  
  // Show CC recipient screen (not a signing authority)
  if (isCCRecipient) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-lg p-8 bg-white rounded-lg shadow-lg">
          <div className="h-20 w-20 mx-auto mb-6 bg-blue-100 rounded-full flex items-center justify-center">
            <svg className="h-10 w-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 4H6a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-2m-4-1v8m0 0l3-3m-3 3L9 8m-5 5h2.586a1 1 0 01.707.293l2.414 2.414a1 1 0 00.707.293h3.172a1 1 0 00.707-.293l2.414-2.414a1 1 0 01.707-.293H20" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-3">
            You&apos;re Copied on This Document
          </h1>
          <p className="text-gray-600 mb-6">
            {ccMessage || "You have been copied on this document for your records. You are not a signing authority and no action is required from you."}
          </p>
          {envelope && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6 text-left">
              <p className="text-sm text-gray-500 mb-1">Document</p>
              <p className="font-medium text-gray-900">{envelope.title}</p>
              {envelope.status && (
                <p className="text-sm mt-2">
                  <span className="text-gray-500">Status: </span>
                  <span className={`font-medium ${
                    envelope.status === 'completed' ? 'text-green-600' :
                    envelope.status === 'sent' ? 'text-blue-600' : 'text-gray-600'
                  }`}>
                    {envelope.status.charAt(0).toUpperCase() + envelope.status.slice(1)}
                  </span>
                </p>
              )}
            </div>
          )}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              Once all signers have completed signing, you will receive a copy of the signed document via email.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // REVIEW PAGE - Show all fields on document with resize controls
  if (showReviewPage) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <h1 className="text-2xl font-bold text-gray-900">Review Your Submission</h1>
            <p className="text-sm text-gray-600 mt-1">Review your inputs on the document and adjust sizes if needed</p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Document with Field Overlays */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-lg font-semibold mb-4">Document with Your Inputs</h2>
                <div 
                  ref={containerRef}
                  className="border-2 border-gray-200 rounded-lg bg-gray-50 overflow-auto"
                  style={{ maxHeight: '80vh' }}
                >
                  {documentUrl ? (
                    <Document
                      file={documentUrl}
                      onLoadSuccess={({ numPages }) => {
                        console.log('✅ PDF loaded successfully, pages:', numPages);
                        setNumPages(numPages);
                      }}
                      onLoadError={(error) => {
                        console.error('❌ PDF load error:', error);
                        toast.error('Failed to load PDF document');
                      }}
                      loading={
                        <div className="flex items-center justify-center h-[700px]">
                          <div className="text-center">
                            <Loader2 className="w-12 h-12 animate-spin text-indigo-600 mx-auto mb-4" />
                            <p className="text-gray-600">Loading document...</p>
                          </div>
                        </div>
                      }
                      className="flex flex-col items-center"
                    >
                      {Array.from(new Array(numPages), (el, pageIndex) => {
                        const pageNumber = pageIndex + 1;
                        // Get fields for this page - check both field.page and field.rect.page
                        const pageFields = fields.filter(f => {
                          const fieldPage = f.page || f.rect?.page || 1;
                          return fieldPage === pageNumber;
                        });
                        
                        return (
                          <div key={`page_${pageNumber}`} className="relative mb-4">
                            <Page
                              pageNumber={pageNumber}
                              width={pageWidth}
                              renderTextLayer={false}
                              renderAnnotationLayer={false}
                            />
                            {/* Field Overlays for this page - use inset-0 to match actual PDF page dimensions */}
                            <div
                              className="absolute inset-0 pointer-events-none"
                              data-page-number={pageNumber}
                            >
                              {pageFields.map((field, index) => {
                                const fieldType = field.field_type || field.type;
                                const value = fieldValues[field.id];
                                const scale = getFieldScale(field.id);
                                const fieldIndex = fields.findIndex(f => f.id === field.id);
                                const isAdjusting = reviewFieldIndex === fieldIndex;
                                
                                // Radio, checkbox-group, and all input fields should always render
                                // Only hide if it's a display-only field without value
                                const isInputField = ['text', 'name', 'email', 'date', 'signature', 'initials', 'checkbox', 'title', 'company'].includes(fieldType);
                                const isGroupField = ['radio', 'checkbox-group'].includes(fieldType);
                                if (!value && !isInputField && !isGroupField) return null;
                                
                                // Get coordinates from rect property
                                const rect = field.rect || {};
                                const x = rect.x || field.x || 0;
                                const y = rect.y || field.y || 0;
                                const w = rect.w || field.w || field.width || 0;
                                const h = rect.h || field.h || field.height || 0;
                                
                                return (
                                  <div
                                    key={field.id}
                                    className={`absolute rounded transition-all ${
                                      (fieldType === 'radio' || fieldType === 'checkbox-group')
                                        ? 'border-0 bg-transparent' // Invisible container for radio/checkbox
                                        : (fieldType === 'signature' || fieldType === 'initials') && value
                                          ? 'border border-gray-200 bg-transparent' // Clean border for filled signature/initials
                                          : isAdjusting
                                            ? 'border-4 border-amber-500 bg-amber-100 bg-opacity-30 shadow-lg'
                                            : 'border-2 border-green-500 bg-green-50 bg-opacity-20'
                                    }`}
                                    style={{
                                      left: `${x * 100}%`,
                                      top: `${y * 100}%`,
                                      width: `${w * 100}%`,
                                      height: `${h * 100}%`,
                                      overflow: 'visible', // Allow circles to be visible outside bounds
                                      pointerEvents: (fieldType === 'radio' || fieldType === 'checkbox-group') ? 'none' : 'auto', // Let circles handle clicks
                                      zIndex: 10
                                    }}
                                  >
                                    {/* Inner container for positioning children - relative to establish context */}
                                    <div style={{
                                      position: 'relative',
                                      width: '100%',
                                      height: '100%',
                                      // For radio/checkbox-group, don't center - let absolute positioning work
                                      // For other fields, center the content
                                      display: (fieldType === 'radio' || fieldType === 'checkbox-group') ? 'block' : 'flex',
                                      alignItems: (fieldType === 'radio' || fieldType === 'checkbox-group') ? 'normal' : 'center',
                                      justifyContent: (fieldType === 'radio' || fieldType === 'checkbox-group') ? 'normal' : 'center'
                                    }}>
                              {(fieldType === 'signature' || fieldType === 'initials') && (
                                <div
                                  className="w-full h-full flex items-center justify-center cursor-pointer hover:bg-indigo-50 transition-colors rounded relative"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveFieldForSignature(field);
                                    setShowSignatureModal(true);
                                  }}
                                  style={{ backgroundColor: value ? '#fff' : '#f9fafb' }}
                                >
                                  {value ? (
                                    <img
                                      src={value}
                                      alt={fieldType}
                                      className="object-contain pointer-events-none"
                                      style={{
                                        maxWidth: fieldType === 'initials' ? '40px' : '95%',
                                        maxHeight: fieldType === 'initials' ? '20px' : '95%',
                                        width: 'auto',
                                        height: 'auto'
                                      }}
                                    />
                                  ) : (
                                    <div className="text-gray-400 text-xs text-center px-2 font-medium">
                                      Click to {fieldType === 'signature' ? 'sign' : 'add initials'}
                                    </div>
                                  )}
                                </div>
                              )}
                              {fieldType === 'image' && (
                                <img 
                                  src={value} 
                                  alt="Uploaded"
                                  className="object-contain"
                                  style={{
                                    width: `${100 * scale}%`,
                                    height: `${100 * scale}%`,
                                  }}
                                />
                              )}
                              {(fieldType === 'text' || fieldType === 'name' || fieldType === 'email' || fieldType === 'title' || fieldType === 'company') && (
                                <input
                                  type={fieldType === 'email' ? 'email' : 'text'}
                                  value={value || ''}
                                  onChange={(e) => updateFieldValue(field.id, e.target.value)}
                                  onClick={(e) => e.stopPropagation()}
                                  onFocus={(e) => e.target.select()}
                                  placeholder={field.props?.label || `Enter ${fieldType}`}
                                  autoComplete="off"
                                  className="w-full h-full px-2 py-1 text-sm border-0 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded font-medium"
                                  style={{
                                    fontSize: `${Math.max(0.75, 0.7 * scale)}rem`,
                                    textAlign: 'left',
                                    color: '#1f2937',
                                    minHeight: '24px'
                                  }}
                                />
                              )}
                              {fieldType === 'date' && (() => {
                                const dateFormat = field.dateFormat || field.props?.dateFormat || 'YYYY-MM-DD';
                                const displayValue = value ? formatDateForDisplay(value, dateFormat) : '';

                                return (
                                  <div className="w-full h-full flex items-center px-2 py-1 bg-white rounded">
                                    <input
                                      type="date"
                                      value={value || ''}
                                      onChange={(e) => updateFieldValue(field.id, e.target.value)}
                                      onClick={(e) => e.stopPropagation()}
                                      onFocus={(e) => e.target.showPicker?.()}
                                      autoComplete="off"
                                      className="absolute opacity-0 pointer-events-none"
                                      style={{ width: '1px', height: '1px' }}
                                    />
                                    <div
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        e.currentTarget.previousSibling.showPicker?.();
                                      }}
                                      className="w-full h-full flex items-center cursor-pointer text-sm font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded"
                                      style={{
                                        fontSize: `${Math.max(0.75, 0.7 * scale)}rem`,
                                        color: value ? '#1f2937' : '#9ca3af',
                                        minHeight: '24px'
                                      }}
                                    >
                                      {displayValue || `Select date (${dateFormat})`}
                                    </div>
                                  </div>
                                );
                              })()}
                              {fieldType === 'checkbox' && (() => {
                                const isChecked = value === true || value === 'true';
                                return (
                                  <div className="flex items-center justify-center w-full h-full">
                                    <div className={`w-6 h-6 border-2 rounded flex items-center justify-center ${isChecked ? 'bg-indigo-600 border-indigo-600' : 'bg-white border-gray-600'}`}>
                                      {isChecked && (
                                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                                        </svg>
                                      )}
                                    </div>
                                  </div>
                                );
                              })()}
                              {/* Radio/checkbox circles are now rendered directly on page container (see below) */}
                              {isAdjusting && (
                                <div className="absolute -top-8 left-0 right-0 text-center pointer-events-auto">
                                  <span className="bg-amber-500 text-white text-xs px-3 py-1 rounded-full font-medium shadow-lg">
                                    Adjusting Size
                                  </span>
                                </div>
                              )}
                                    </div>{/* Close inner relative container */}
                                  </div>
                                );
                              })}

                              {/* Render radio/checkbox circles directly on page (not inside field containers) */}
                              {pageFields.filter(f => ['radio', 'checkbox-group'].includes(f.field_type || f.type)).map(field => {
                                const fieldType = field.field_type || field.type;
                                const value = fieldValues[field.id];

                                // Get field container position
                                const rect = field.rect || {};
                                const fieldX = rect.x || field.x || 0;
                                const fieldY = rect.y || field.y || 0;
                                const fieldW = rect.w || field.w || field.width || 0;
                                const fieldH = rect.h || field.h || field.height || 0;

                                return (field.props?.options || []).map((option, optionIndex) => {
                                  const isSelected = fieldType === 'radio' ? value === option.value : (Array.isArray(value) ? value.includes(option.value) : false);

                                  // CRITICAL: Options are stored as CONTAINER-RELATIVE ratios (0-1 relative to field container)
                                  // Convert to PAGE-RELATIVE by adding field position
                                  const containerRelX = option.x || 0;
                                  const containerRelY = option.y || 0;

                                  // Convert to page-relative coordinates
                                  const pageRelX = fieldX + (containerRelX * fieldW);
                                  const pageRelY = fieldY + (containerRelY * fieldH);

                                  const optX = pageRelX * 100;
                                  const optY = pageRelY * 100;

                                  // Debug log
                                  if (optionIndex === 0) {
                                    console.log('🔵 REVIEW Radio positioning:', {
                                      fieldId: field.id,
                                      pageNumber,
                                      'field.x': fieldX,
                                      'field.y': fieldY,
                                      'field.w': fieldW,
                                      'field.h': fieldH,
                                      'option.x (container-rel)': containerRelX,
                                      'option.y (container-rel)': containerRelY,
                                      'pageRelX': pageRelX,
                                      'pageRelY': pageRelY,
                                      'optX%': optX,
                                      'optY%': optY
                                    });
                                  }

                                  return (
                                    <div
                                      key={`${field.id}_opt_${optionIndex}`}
                                      style={{
                                        position: 'absolute',
                                        left: `${optX}%`,
                                        top: `${optY}%`,
                                        zIndex: 100,
                                        cursor: 'pointer',
                                        pointerEvents: 'auto'
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (fieldType === 'radio') {
                                          updateFieldValue(field.id, option.value);
                                        } else {
                                          const currentValues = Array.isArray(value) ? value : [];
                                          const newValues = currentValues.includes(option.value)
                                            ? currentValues.filter(v => v !== option.value)
                                            : [...currentValues, option.value];
                                          updateFieldValue(field.id, newValues);
                                        }
                                      }}
                                    >
                                      {fieldType === 'radio' ? (
                                        <div className={`w-3 h-3 rounded-full border-2 flex items-center justify-center bg-white shadow-sm ${isSelected ? 'border-indigo-600' : 'border-gray-600'}`}>
                                          {isSelected && (
                                            <div className="w-1.5 h-1.5 rounded-full bg-indigo-600"></div>
                                          )}
                                        </div>
                                      ) : (
                                        <div className={`w-3 h-3 border-2 rounded flex items-center justify-center shadow-sm ${isSelected ? 'bg-indigo-600 border-indigo-600' : 'bg-white border-gray-600'}`}>
                                          {isSelected && (
                                            <svg className="w-2 h-2 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                                            </svg>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  );
                                });
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </Document>
                  ) : (
                    <div className="flex items-center justify-center h-[700px]">
                      <p className="text-gray-500">Document preview not available</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Field List with Size Controls */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-sm p-6 sticky top-4 max-h-[calc(100vh-100px)] overflow-y-auto">
                <h3 className="text-lg font-semibold mb-4">Your Fields</h3>
                <div className="space-y-4">
                  {fields.map((field, index) => {
                    const fieldType = field.field_type || field.type;
                    const props = field.props || field.properties || {};
                    const value = fieldValues[field.id];
                    const scale = getFieldScale(field.id);
                    const isAdjusting = reviewFieldIndex === index;

                    if (!isFieldValueSet(field, value)) return null;
                    
                    return (
                      <div 
                        key={field.id} 
                        className={`border-2 rounded-lg p-4 transition-all ${
                          isAdjusting ? 'border-amber-500 bg-amber-50' : 'border-gray-200 bg-white'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <span className="inline-block px-2 py-1 text-xs font-bold rounded uppercase"
                              style={{
                                backgroundColor: fieldType === 'signature' ? '#f3e8ff' : 
                                               fieldType === 'initials' ? '#ede9fe' :
                                               fieldType === 'name' ? '#dbeafe' :
                                               fieldType === 'email' ? '#cffafe' :
                                               '#fef3c7',
                                color: fieldType === 'signature' ? '#7c3aed' :
                                       fieldType === 'initials' ? '#6b21a8' :
                                       fieldType === 'name' ? '#1e40af' :
                                       fieldType === 'email' ? '#0e7490' :
                                       '#d97706'
                              }}>
                              {fieldType}
                            </span>
                            <p className="text-sm font-medium text-gray-700 mt-1">
                              {props.label || `Field ${index + 1}`}
                            </p>
                          </div>
                          <button
                            onClick={() => setReviewFieldIndex(isAdjusting ? null : index)}
                            className={`text-xs px-3 py-1 rounded-md font-medium transition-colors ${
                              isAdjusting 
                                ? 'bg-amber-100 text-amber-700 border-2 border-amber-500' 
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            {isAdjusting ? 'Done' : 'Adjust'}
                          </button>
                        </div>
                        
                        {/* Size Controls - Show when adjusting */}
                        {isAdjusting && (
                          <div className="mt-4 pt-4 border-t border-amber-200 bg-amber-50 rounded-lg p-3">
                            <div className="flex items-center justify-between mb-2">
                              <label className="text-sm font-medium text-amber-900">Resize</label>
                              <span className="text-xs bg-amber-200 text-amber-900 px-2 py-1 rounded-full font-bold">
                                {Math.round(scale * 100)}%
                              </span>
                            </div>
                            <div className="flex items-center gap-2 mb-2">
                              <button
                                onClick={() => updateFieldScale(field.id, Math.max(0.5, scale - 0.1))}
                                className="px-3 py-2 bg-white border-2 border-amber-300 rounded-lg hover:bg-amber-50 text-lg font-bold"
                              >
                                −
                              </button>
                              <input
                                type="range"
                                min="0.5"
                                max="2.5"
                                step="0.1"
                                value={scale}
                                onChange={(e) => updateFieldScale(field.id, parseFloat(e.target.value))}
                                className="flex-1 accent-amber-600"
                              />
                              <button
                                onClick={() => updateFieldScale(field.id, Math.min(2.5, scale + 0.1))}
                                className="px-3 py-2 bg-white border-2 border-amber-300 rounded-lg hover:bg-amber-50 text-lg font-bold"
                              >
                                +
                              </button>
                            </div>
                            <div className="flex justify-between text-xs text-amber-700">
                              <span>50%</span>
                              <button
                                onClick={() => updateFieldScale(field.id, 1.0)}
                                className="underline hover:text-amber-900 font-medium"
                              >
                                Reset
                              </button>
                              <span>250%</span>
                            </div>
                          </div>
                        )}
                        
                        {/* Field Preview Thumbnail */}
                        {!isAdjusting && (
                          <div className="mt-3 p-2 bg-gray-50 rounded border border-gray-200">
                            {(fieldType === 'signature' || fieldType === 'initials' || fieldType === 'image') && (
                              <img src={value} alt={fieldType} className="max-h-16 mx-auto" />
                            )}
                            {(fieldType === 'text' || fieldType === 'name' || fieldType === 'email' || fieldType === 'date' || fieldType === 'title' || fieldType === 'company') && (() => {
                              const maxLength = field.props?.maxLength || field.properties?.maxLength;
                              const isOverMaxLength = maxLength && value && value.length > maxLength;
                              // Format date for display
                              let displayValue = value;
                              if (fieldType === 'date' && value) {
                                // Check for 'format' property (used in editor) as well as 'dateFormat'
                                const dateFormat = field.props?.format || field.properties?.format || field.dateFormat || field.props?.dateFormat || field.properties?.dateFormat || 'YYYY-MM-DD';
                                displayValue = formatDateForDisplay(value, dateFormat);
                              }
                              return (
                                <div>
                                  <p className={`text-sm text-center break-all ${isOverMaxLength ? 'text-red-600' : 'text-gray-700'}`}>{displayValue}</p>
                                  {isOverMaxLength && (
                                    <p className="text-xs text-red-600 text-center mt-1 font-medium">
                                      ⚠ The text is too long ({value.length}/{maxLength} chars)
                                    </p>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 mt-6 pt-6 border-t border-gray-200">
                  <button
                    onClick={handleBackToEdit}
                    className="flex-1 px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
                  >
                    Back to Edit
                  </button>
                  <button
                    onClick={handleFinalSubmit}
                    disabled={isSubmitting}
                    className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2 font-medium"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="animate-spin" size={18} />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <CheckCircle size={18} />
                        Confirm & Submit
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  const currentField = fields[currentFieldIndex];
  // Progress bar reflects actual completion (how many fields are filled in),
  // matching the "X/Y" counter at the bottom — not navigation position.
  const completedFieldCount = fields.filter(f => isFieldValueSet(f, fieldValues[f.id])).length;
  const progress = fields.length > 0 ? (completedFieldCount / fields.length) * 100 : 0;
  
  return (
    <>
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-semibold text-gray-900">
                {envelope?.title || 'Sign Document'}
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                Please review and complete all required fields
              </p>
            </div>
            <div className="text-right">
              <div className="text-sm font-medium text-gray-700">
                Field {currentFieldIndex + 1} of {fields.length}
              </div>
              <div className="w-48 h-2 bg-gray-200 rounded-full mt-2">
                <div 
                  className="h-full bg-indigo-600 rounded-full transition-all"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 py-8 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          {/* Fields Sidebar - Sticky to stay visible while scrolling */}
          <div className="lg:col-span-3 lg:sticky lg:top-4 bg-white rounded-lg shadow-sm p-4 z-10">
            <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              All Fields ({fields.length})
            </h3>
            <div className="space-y-2 max-h-[calc(100vh-200px)] overflow-y-auto">
              {fields.map((field, index) => {
                const fieldType = field.type || field.field_type || 'field';
                // Get proper field label - capitalize first letter
                const typeLabel = fieldType.charAt(0).toUpperCase() + fieldType.slice(1);
                // Clean up label - remove stray template artifacts like }}
                let rawLabel = field.props?.label || field.label || typeLabel;
                // Filter out labels that are only curly braces or template artifacts
                if (rawLabel && typeof rawLabel === 'string') {
                  rawLabel = rawLabel.replace(/^\s*\}\}+\s*$/, '').replace(/^\s*\{\{+\s*$/, '').trim();
                }
                const fieldLabel = rawLabel || typeLabel;
                const isCurrent = index === currentFieldIndex;
                const isCompleted = isFieldValueSet(field, fieldValues[field.id]);
                
                const fieldColors = {
                  'signature': 'bg-purple-50 border-purple-200 text-purple-700',
                  'text': 'bg-blue-50 border-blue-200 text-blue-700',
                  'date': 'bg-orange-50 border-orange-200 text-orange-700',
                  'name': 'bg-cyan-50 border-cyan-200 text-cyan-700',
                  'email': 'bg-green-50 border-green-200 text-green-700',
                  'initials': 'bg-pink-50 border-pink-200 text-pink-700'
                };
                const colorClass = fieldColors[fieldType] || 'bg-gray-50 border-gray-200 text-gray-700';
                
                return (
                  <button
                    key={field.id || index}
                    onClick={() => {
                      setCurrentFieldIndex(index);
                      setTimeout(() => scrollToFieldPosition(index), 300);
                      toast.success(`Navigated to ${fieldLabel}`);
                    }}
                    className={`w-full text-left px-3 py-2 rounded-md border transition-all ${colorClass} 
                      ${isCurrent ? 'ring-2 ring-indigo-500 shadow-md' : 'hover:shadow-sm'}
                      ${isCompleted ? 'opacity-60' : ''}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium truncate">{fieldLabel}</div>
                        <div className="text-xs opacity-75 mt-0.5">
                          Page {field.page || field.rect?.page || 1}
                        </div>
                      </div>
                      {isCompleted && (
                        <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 ml-2" />
                      )}
                      {isCurrent && !isCompleted && (
                        <div className="w-2 h-2 bg-indigo-600 rounded-full flex-shrink-0 ml-2 animate-pulse"></div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
          
          {/* Document with Field Overlays */}
          <div className="lg:col-span-9">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold mb-4">Document</h2>
              <div
                ref={containerRef}
                className="border-2 border-gray-200 rounded-lg bg-gray-50 overflow-auto"
                style={{ maxHeight: '800px' }}
              >
                <div className="flex justify-center">
                  {documentUrl ? (
                    <>
                      <Document
                        file={documentUrl}
                        onLoadSuccess={onDocumentLoadSuccess}
                        loading={
                          <div className="flex items-center justify-center p-8">
                            <Loader2 className="animate-spin" size={32} />
                          </div>
                        }
                        className="flex justify-center"
                      >
                        {/* Wrap Page and overlays in a single relative container */}
                        <div className="relative">
                          <Page
                            pageNumber={pageNumber}
                            width={pageWidth}
                            renderTextLayer={false}
                            renderAnnotationLayer={false}
                          />

                          {/* Field Overlays - positioned relative to PDF page */}
                          <div className="absolute inset-0 pointer-events-none">
                            {fields
                              .filter(field => {
                                // Check both field.page and field.rect.page
                                const page = field.page || (field.rect && field.rect.page);
                                return page === pageNumber;
                              })
                              .map((field, index) => {
                                const isCurrentField = currentFieldIndex === fields.findIndex(f => f.id === field.id);
                                const fieldValue = fieldValues[field.id];
                                const fieldType = field.field_type || field.type;

                                // Get field coordinates - handle both rect object and direct properties
                                const rect = field.rect || {};
                                const x = rect.x !== undefined ? rect.x : field.x || 0;
                                const y = rect.y !== undefined ? rect.y : field.y || 0;
                                const w = rect.w !== undefined ? rect.w : field.width || 0;
                                const h = rect.h !== undefined ? rect.h : field.height || 0;

                                return (
                                  <div
                                    key={field.id || index}
                                    ref={(el) => {
                                      if (el && field.id) {
                                        fieldRefs.current[field.id] = el;
                                      }
                                    }}
                                    onClick={() => {
                                      const fieldIndex = fields.findIndex(f => f.id === field.id);
                                      setCurrentFieldIndex(fieldIndex);

                                      // Handle different field types
                                      if (fieldType === 'signature' || fieldType === 'initials') {
                                        setActiveFieldForSignature(field);
                                        setShowSignatureModal(true);
                                      } else if (fieldType === 'text' || fieldType === 'name' || fieldType === 'email' || fieldType === 'date' || fieldType === 'title' || fieldType === 'company') {
                                        setEditingFieldId(field.id);
                                      }
                                    }}
                                    style={{
                                      position: 'absolute',
                                      left: `${x * 100}%`,
                                      top: `${y * 100}%`,
                                      width: `${w * 100}%`,
                                      height: `${h * 100}%`,
                                      backgroundColor: (fieldType === 'checkbox' || fieldType === 'radio' || fieldType === 'checkbox-group') ? 'transparent' :
                                                      ((fieldType === 'signature' || fieldType === 'initials') && fieldValue) ? 'transparent' :
                                                      (fieldValue ? '#d1fae5' : (isCurrentField ? '#fef3c7' : '#ddd6fe')),
                                      border: (fieldType === 'checkbox' || fieldType === 'radio' || fieldType === 'checkbox-group') ? 'none' :
                                             ((fieldType === 'signature' || fieldType === 'initials') && fieldValue) ? '1px solid #e5e7eb' :
                                             (isCurrentField ? '3px solid #7c3aed' : (fieldValue ? '2px solid #10b981' : '2px dashed #a78bfa')),
                                      borderRadius: '4px',
                                      cursor: 'pointer',
                                      // For radio/checkbox-group, don't center - let absolute positioning work
                                      // For other fields, center the content
                                      display: (fieldType === 'radio' || fieldType === 'checkbox-group') ? 'block' : 'flex',
                                      alignItems: (fieldType === 'radio' || fieldType === 'checkbox-group') ? 'normal' : 'center',
                                      justifyContent: (fieldType === 'radio' || fieldType === 'checkbox-group') ? 'normal' : 'center',
                                      fontSize: '12px',
                                      fontWeight: '600',
                                      color: '#4c1d95',
                                      transition: 'all 0.2s',
                                      zIndex: isCurrentField ? 20 : 10,
                                      overflow: 'hidden',
                                      pointerEvents: 'auto'
                                    }}
                                    className="hover:shadow-lg"
                                  >
                              {/* Signature/Initials display */}
                              {(fieldType === 'signature' || fieldType === 'initials') && fieldValue && (
                                <img
                                  src={fieldValue}
                                  alt={fieldType}
                                  className={fieldType === 'initials' ? 'object-contain' : 'absolute inset-0 w-full h-full object-contain'}
                                  style={{
                                    pointerEvents: 'none',
                                    maxWidth: fieldType === 'initials' ? '40px' : '100%',
                                    maxHeight: fieldType === 'initials' ? '20px' : '100%',
                                    position: fieldType === 'initials' ? 'relative' : 'absolute',
                                    margin: fieldType === 'initials' ? 'auto' : '0'
                                  }}
                                />
                              )}
                              
                              {fieldType === 'checkbox' ? (() => {
                                // Normalize to a strict boolean so any legacy non-boolean
                                // default value (e.g. a stray []) can't desync the visual
                                // checked state from the required-field completion logic.
                                const isChecked = fieldValue === true || fieldValue === 'true';
                                return (
                                  <div
                                    className="flex items-center justify-center w-full h-full cursor-pointer"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      updateFieldValue(field.id, !isChecked);
                                    }}
                                  >
                                    <div className={`w-6 h-6 border-2 rounded flex items-center justify-center ${isChecked ? 'bg-indigo-600 border-indigo-600' : 'bg-white border-gray-600'}`}>
                                      {isChecked && (
                                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                                        </svg>
                                      )}
                                    </div>
                                  </div>
                                );
                              })() : null}
                              
                              {(fieldType === 'radio' || fieldType === 'checkbox-group') && (
                                <>
                                  {(field.props?.options || []).map((option, optionIndex) => {
                                    const isSelected = fieldType === 'radio' ? fieldValue === option.value : (Array.isArray(fieldValue) ? fieldValue.includes(option.value) : false);

                                    // Option x,y are ratios (0-1) within the field container
                                    // These represent the TOP-LEFT corner of the circle (matching editor behavior)
                                    // If x,y are missing, use vertical layout defaults
                                    const defaultX = 0.1;  // 10% from left
                                    const defaultY = 0.15 + (optionIndex * 0.35);  // Stacked vertically
                                    const optX = (option.x !== undefined && option.x !== null ? option.x : defaultX) * 100;
                                    const optY = (option.y !== undefined && option.y !== null ? option.y : defaultY) * 100;

                                    return (
                                      <div
                                        key={option.id || `opt_${optionIndex}`}
                                        style={{
                                          position: 'absolute',
                                          left: `${optX}%`,
                                          top: `${optY}%`,
                                          // NO transform - position is top-left corner, matching editor
                                          zIndex: 100,
                                          cursor: 'pointer',
                                          pointerEvents: 'auto'
                                        }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (fieldType === 'radio') {
                                            updateFieldValue(field.id, option.value);
                                          } else {
                                            const currentValues = Array.isArray(fieldValue) ? fieldValue : [];
                                            const newValues = currentValues.includes(option.value)
                                              ? currentValues.filter(v => v !== option.value)
                                              : [...currentValues, option.value];
                                            updateFieldValue(field.id, newValues);
                                          }
                                        }}
                                      >
                                        {fieldType === 'radio' ? (
                                          <div className={`w-3 h-3 rounded-full border-2 flex items-center justify-center bg-white shadow-sm ${isSelected ? 'border-indigo-600' : 'border-gray-600'}`}>
                                            {isSelected && (
                                              <div className="w-1.5 h-1.5 rounded-full bg-indigo-600"></div>
                                            )}
                                          </div>
                                        ) : (
                                          <div className={`w-3 h-3 border-2 rounded flex items-center justify-center shadow-sm ${isSelected ? 'bg-indigo-600 border-indigo-600' : 'bg-white border-gray-600'}`}>
                                            {isSelected && (
                                              <svg className="w-2 h-2 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                                              </svg>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </>
                              )}
                              
                              {/* Inline input rendering for text, name, email, date, title, and company fields */}
                              {editingFieldId === field.id && (fieldType === 'text' || fieldType === 'name' || fieldType === 'email' || fieldType === 'date' || fieldType === 'title' || fieldType === 'company') && (() => {
                                const maxLength = field.props?.maxLength || field.properties?.maxLength;
                                const isOverMaxLength = maxLength && fieldValue && fieldValue.length > maxLength;

                                return (
                                  <div
                                    className="absolute inset-0 z-50"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {/* Warning tooltip while editing */}
                                    {isOverMaxLength && (
                                      <div
                                        className="absolute z-50"
                                        style={{
                                          bottom: '100%',
                                          left: '50%',
                                          transform: 'translateX(-50%)',
                                          marginBottom: '4px',
                                          pointerEvents: 'none'
                                        }}
                                      >
                                        <div
                                          className="bg-red-600 text-white text-xs px-3 py-2 rounded shadow-lg text-center"
                                          style={{
                                            minWidth: '180px',
                                            maxWidth: '250px',
                                            lineHeight: '1.4'
                                          }}
                                        >
                                          The text is too long for this field.<br/>
                                          Hence part of it has been omitted.
                                        </div>
                                        {/* Arrow pointing down */}
                                        <div
                                          style={{
                                            position: 'absolute',
                                            bottom: '-6px',
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            width: 0,
                                            height: 0,
                                            borderLeft: '6px solid transparent',
                                            borderRight: '6px solid transparent',
                                            borderTop: '6px solid #dc2626'
                                          }}
                                        />
                                      </div>
                                    )}
                                    {fieldType === 'date' ? (
                                      <input
                                        type="date"
                                        value={fieldValue || ''}
                                        onChange={(e) => updateFieldValue(field.id, e.target.value)}
                                        onBlur={() => setEditingFieldId(null)}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter' || e.key === 'Escape') {
                                            setEditingFieldId(null);
                                          }
                                        }}
                                        autoFocus
                                        className="w-full h-full px-2 py-1 text-sm border-2 border-indigo-500 bg-white focus:outline-none rounded font-medium"
                                        style={{
                                          fontSize: '12px',
                                          color: '#1f2937',
                                          minHeight: '24px'
                                        }}
                                      />
                                    ) : (
                                      <input
                                        type={fieldType === 'email' ? 'email' : 'text'}
                                        value={fieldValue || ''}
                                        onChange={(e) => {
                                          const newValue = e.target.value;
                                          // Update the value (don't prevent typing, just warn)
                                          updateFieldValue(field.id, newValue);
                                          // Check if maxLength is exceeded
                                          if (maxLength && newValue.length > maxLength) {
                                            setTextOverflowWarnings(prev => ({ ...prev, [field.id]: true }));
                                          } else {
                                            setTextOverflowWarnings(prev => ({ ...prev, [field.id]: false }));
                                          }
                                        }}
                                        onBlur={() => setEditingFieldId(null)}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter' || e.key === 'Escape') {
                                            setEditingFieldId(null);
                                          }
                                        }}
                                        autoFocus
                                        placeholder={field.props?.label || `Enter ${fieldType}`}
                                        className={`w-full h-full px-2 py-1 text-sm border-2 bg-white focus:outline-none rounded font-medium ${
                                          isOverMaxLength ? 'border-red-500' : 'border-indigo-500'
                                        }`}
                                        style={{
                                          fontSize: '12px',
                                          color: '#1f2937',
                                          minHeight: '24px'
                                        }}
                                      />
                                    )}
                                  </div>
                                );
                              })()}
                              
                                  {/* Display field value when not editing - truncate if over maxLength */}
                                  {editingFieldId !== field.id && (fieldType === 'text' || fieldType === 'name' || fieldType === 'email' || fieldType === 'date' || fieldType === 'title' || fieldType === 'company') && fieldValue && (() => {
                                    const maxLength = field.props?.maxLength || field.properties?.maxLength;
                                    const isOverMaxLength = maxLength && fieldValue.length > maxLength;
                                    // Format date or truncate text if over maxLength
                                    let displayText;
                                    if (fieldType === 'date') {
                                      // Check for 'format' property (used in editor) as well as 'dateFormat'
                                      const dateFormat = field.props?.format || field.properties?.format || field.dateFormat || field.props?.dateFormat || field.properties?.dateFormat || 'YYYY-MM-DD';
                                      displayText = formatDateForDisplay(fieldValue, dateFormat);
                                    } else {
                                      displayText = isOverMaxLength ? fieldValue.substring(0, maxLength) : fieldValue;
                                    }

                                    // Update warning state if needed
                                    if (isOverMaxLength !== textOverflowWarnings[field.id]) {
                                      setTimeout(() => {
                                        setTextOverflowWarnings(prev => ({ ...prev, [field.id]: isOverMaxLength }));
                                      }, 0);
                                    }

                                    return (
                                      <>
                                        {/* Warning tooltip - positioned above field */}
                                        {isOverMaxLength && (
                                          <div
                                            className="absolute z-50"
                                            style={{
                                              bottom: '100%',
                                              left: '50%',
                                              transform: 'translateX(-50%)',
                                              marginBottom: '4px',
                                              pointerEvents: 'none'
                                            }}
                                          >
                                            <div
                                              className="bg-red-600 text-white text-xs px-3 py-2 rounded shadow-lg text-center"
                                              style={{
                                                minWidth: '180px',
                                                maxWidth: '250px',
                                                lineHeight: '1.4'
                                              }}
                                            >
                                              The text is too long for this field.<br/>
                                              Hence part of it has been omitted.
                                            </div>
                                            {/* Arrow pointing down */}
                                            <div
                                              style={{
                                                position: 'absolute',
                                                bottom: '-6px',
                                                left: '50%',
                                                transform: 'translateX(-50%)',
                                                width: 0,
                                                height: 0,
                                                borderLeft: '6px solid transparent',
                                                borderRight: '6px solid transparent',
                                                borderTop: '6px solid #dc2626'
                                              }}
                                            />
                                          </div>
                                        )}
                                        {/* Truncated text display */}
                                        <div
                                          className="absolute inset-0 flex items-center px-2 font-medium"
                                          style={{ overflow: 'hidden' }}
                                        >
                                          <span
                                            style={{
                                              whiteSpace: 'nowrap',
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis',
                                              fontSize: '14px',
                                              color: '#1f2937'
                                            }}
                                          >
                                            {displayText}
                                          </span>
                                        </div>
                                      </>
                                    );
                                  })()}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </Document>
                    </>
                  ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Page Navigation */}
                {numPages && numPages > 1 && (
                  <div className="flex items-center justify-center gap-4 p-4 bg-white border-t">
                    <button
                      onClick={() => {
                        const target = Math.max(pageNumber - 1, 1);
                        setPageNumber(target);
                        // Keep currentFieldIndex in sync with manual page navigation so that
                        // Field Next/Prev and sidebar clicks don't resume from a stale index
                        // on a different page and snap the view backward.
                        const idx = fields.findIndex(f => (f.page || f.rect?.page || 1) === target);
                        if (idx !== -1) setCurrentFieldIndex(idx);
                      }}
                      disabled={pageNumber <= 1}
                      className="px-3 py-1 border rounded disabled:opacity-50"
                      aria-label="Previous page"
                    >
                      <ChevronLeft size={16} aria-hidden="true" />
                    </button>
                    <span className="text-sm text-gray-600" aria-live="polite">
                      Page {pageNumber} of {numPages}
                    </span>
                    <button
                      onClick={() => {
                        const target = Math.min(pageNumber + 1, numPages);
                        setPageNumber(target);
                        // Keep currentFieldIndex in sync with manual page navigation so that
                        // Field Next/Prev and sidebar clicks don't resume from a stale index
                        // on a different page and snap the view backward.
                        const idx = fields.findIndex(f => (f.page || f.rect?.page || 1) === target);
                        if (idx !== -1) setCurrentFieldIndex(idx);
                      }}
                      disabled={pageNumber >= numPages}
                      className="px-3 py-1 border rounded disabled:opacity-50"
                      aria-label="Next page"
                    >
                      <ChevronRight size={16} aria-hidden="true" />
                    </button>
                  </div>
                )}
        
        {/* Bottom Navigation Bar */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-40" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
          <div className="max-w-7xl mx-auto px-3 py-2 sm:py-4">
            <div className="flex items-center justify-between gap-2">
              {/* Decline Button on Left */}
              <button
                onClick={() => setShowDeclineModal(true)}
                className="px-3 py-2 sm:px-6 sm:py-3 border-2 border-red-300 text-red-600 rounded-lg hover:bg-red-50 flex items-center gap-1 sm:gap-2 font-medium transition-all text-sm sm:text-base flex-shrink-0"
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span className="hidden xs:inline">Decline</span>
              </button>

              {/* Center Navigation */}
              <div className="flex items-center gap-2 sm:gap-4 flex-1 justify-center">
                <button
                  onClick={goToPreviousField}
                  disabled={currentFieldIndex === 0}
                  className="px-3 py-2 sm:px-8 sm:py-3 border-2 border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 font-medium transition-all text-sm sm:text-base"
                >
                  <ChevronLeft size={18} />
                  <span className="hidden sm:inline">Prev</span>
                </button>

                <div className="px-3 py-2 sm:px-6 sm:py-3 bg-gray-100 rounded-lg border border-gray-300 text-center min-w-0">
                  <div className="text-xs sm:text-sm font-medium text-gray-700 whitespace-nowrap">
                    {fields.length} Fields
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {fields.filter(f => isFieldValueSet(f, fieldValues[f.id])).length}/{fields.length}
                  </div>
                </div>

                {areAllRequiredFieldsFilled() ? (
                  <button
                    onClick={handleShowReview}
                    className="px-3 py-2 sm:px-8 sm:py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-1 sm:gap-2 font-medium transition-all shadow-md text-sm sm:text-base"
                  >
                    <CheckCircle size={18} />
                    <span className="hidden sm:inline">Review &amp; Submit</span>
                    <span className="sm:hidden">Submit</span>
                  </button>
                ) : (
                  <button
                    onClick={goToNextField}
                    disabled={currentFieldIndex >= fields.length - 1}
                    className="px-3 py-2 sm:px-8 sm:py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 font-medium transition-all text-sm sm:text-base"
                  >
                    <span className="hidden sm:inline">Next</span>
                    <ChevronRight size={18} />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>
      
      {/* Decline Confirmation Modal */}
      {showDeclineModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-center mb-4">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
              </div>
              
              <h2 className="text-xl font-bold text-gray-900 text-center mb-2">
                Decline Signing
              </h2>
              <p className="text-gray-600 text-center mb-6">
                Are you sure you want to decline signing this document? The sender will be notified of your decision.
              </p>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeclineModal(false)}
                  disabled={isDeclining}
                  className="flex-1 px-6 py-3 border-2 border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-all disabled:opacity-50"
                >
                  No, Continue Signing
                </button>
                
                <button
                  onClick={async () => {
                    setIsDeclining(true);
                    try {
                      await axios.post(`${API}/signing-sessions/${token}/decline`, {}, {
                        headers: { 'Content-Type': 'application/json' }
                      });
                      toast.success('Document declined. Sender has been notified.');
                      setShowDeclineModal(false);
                      setIsDeclined(true);
                      setTimeout(() => {
                        setIsCompleted(true);
                      }, 2000);
                    } catch (error) {
                      console.error('Failed to decline:', error);
                      toast.error('Failed to decline document');
                      setIsDeclining(false);
                    }
                  }}
                  disabled={isDeclining}
                  className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isDeclining ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Declining...
                    </>
                  ) : (
                    'Yes, Decline'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Signature Modal */}
      {showSignatureModal && activeFieldForSignature && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">
                  Add {activeFieldForSignature.type === 'initials' ? 'Initials' : 'Signature'}
                </h2>
                <button
                  onClick={() => {
                    setShowSignatureModal(false);
                    setActiveFieldForSignature(null);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                  aria-label="Close signature modal"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {/* Color Picker */}
              <div className="mb-4 pb-4 border-b border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Signature Color
                </label>
                <div className="flex gap-3">
                  {[
                    { color: '#000000', name: 'Black' },
                    { color: '#0000FF', name: 'Blue' },
                    { color: '#FF0000', name: 'Red' },
                    { color: '#008000', name: 'Green' }
                  ].map((colorOption) => (
                    <button
                      key={colorOption.color}
                      onClick={() => setSignatureColor(colorOption.color)}
                      className={`w-10 h-10 rounded-full border-2 transition-all ${
                        signatureColor === colorOption.color
                          ? 'border-indigo-600 ring-2 ring-indigo-200 scale-110'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                      style={{ backgroundColor: colorOption.color }}
                      title={colorOption.name}
                      aria-label={`Select ${colorOption.name} signature color`}
                    >
                      {signatureColor === colorOption.color && (
                        <svg className="w-6 h-6 mx-auto text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              </div>
              
              <SignatureCanvas
                color={signatureColor}
                onSave={(signatureData) => {
                  const currentFieldType = activeFieldForSignature.field_type || activeFieldForSignature.type;

                  // Update the current field
                  updateFieldValue(activeFieldForSignature.id, signatureData);

                  // Auto-populate all other signature fields (not initials) with the same signature
                  if (currentFieldType === 'signature') {
                    console.log('🖊️ Auto-populating all signature fields with saved signature');
                    const signatureFields = fields.filter(f => {
                      const fType = f.field_type || f.type;
                      return fType === 'signature' && f.id !== activeFieldForSignature.id;
                    });

                    signatureFields.forEach(field => {
                      updateFieldValue(field.id, signatureData);
                      console.log(`✅ Auto-populated signature field ${field.id}`);
                    });

                    if (signatureFields.length > 0) {
                      toast.success(`Signature applied to ${signatureFields.length + 1} field(s)`);
                    }
                  }

                  setShowSignatureModal(false);
                  setActiveFieldForSignature(null);
                  // Auto-advance to next field
                  if (currentFieldIndex < fields.length - 1) {
                    setTimeout(() => goToNextField(), 500);
                  }
                }}
                onCancel={() => {
                  setShowSignatureModal(false);
                  setActiveFieldForSignature(null);
                }}
              />
            </div>
          </div>
        </div>
      )}

    </>
  );
};

// Field Input Component
const FieldInput = ({ field, value, onChange }) => {
  const [signatureData, setSignatureData] = useState(null);
  
  const renderInput = () => {
    const fieldType = field.field_type || field.type;
    const props = field.props || field.properties || {};
    console.log('🎯 Rendering field:', { 
      id: field.id, 
      field_type: field.field_type, 
      type: field.type,
      resolved_type: fieldType,
      props,
      full_field: field 
    });
    
    switch (fieldType) {
      case 'signature':
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {props.label || 'Signature'}
              {props.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <SignatureCanvas
              onSave={(data) => {
                setSignatureData(data);
                onChange(data);
              }}
            />
            {signatureData && (
              <div className="mt-2">
                <img src={signatureData} alt="Signature" className="border rounded max-w-full" />
              </div>
            )}
          </div>
        );
        
      case 'text':
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {props.label || 'Text'}
              {props.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type="text"
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Enter text"
            />
          </div>
        );
        
      case 'date':
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {props.label || 'Date'}
              {props.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type="date"
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
        );
        
      case 'checkbox':
        return (
          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={value === true || value === 'true'}
                onChange={(e) => onChange(e.target.checked)}
                className="w-5 h-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
              />
              <span className="text-sm font-medium text-gray-700">
                {props.label || 'Checkbox'}
                {props.required && <span className="text-red-500 ml-1">*</span>}
              </span>
            </label>
          </div>
        );
        
      case 'name':
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {props.label || 'Name'}
              {props.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type="text"
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Enter your name"
            />
          </div>
        );
        
      case 'email':
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {props.label || 'Email'}
              {props.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type="email"
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Enter your email"
            />
          </div>
        );
        
      case 'initials':
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {props.label || 'Initials'}
              {props.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <SignatureCanvas
              onSave={(data) => {
                setSignatureData(data);
                onChange(data);
              }}
              width={200}
              height={80}
            />
            {signatureData && (
              <div className="mt-2">
                <img src={signatureData} alt="Initials" className="border rounded max-w-full" />
              </div>
            )}
          </div>
        );
        
      case 'image':
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {props.label || 'Image/Stamp'}
              {props.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  const reader = new FileReader();
                  reader.onloadend = () => {
                    onChange(reader.result);
                  };
                  reader.readAsDataURL(file);
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            />
            {value && (
              <div className="mt-2">
                <img src={value} alt="Uploaded" className="border rounded max-w-full max-h-40 object-contain" />
              </div>
            )}
          </div>
        );
        
      case 'radio':
        const options = props.options || [];
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {props.label || 'Radio Group'}
              {props.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <div className="space-y-2">
              {options.map(option => (
                <label key={option.id} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name={field.id}
                    value={option.value}
                    checked={value === option.value}
                    onChange={(e) => onChange(e.target.value)}
                    className="w-4 h-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                  />
                  <span className="text-sm text-gray-700">{option.label}</span>
                </label>
              ))}
            </div>
          </div>
        );
        
      default:
        return <p className="text-gray-500">Unsupported field type: {fieldType}</p>;
    }
  };
  
  const fieldType = field.field_type || field.type;
  
  return (
    <div>
      <div className="mb-4">
        <span className="inline-block px-2 py-1 text-xs font-medium rounded uppercase" 
          style={{
            backgroundColor: fieldType === 'signature' ? '#f3e8ff' :
                           fieldType === 'initials' ? '#ede9fe' :
                           fieldType === 'name' ? '#dbeafe' :
                           fieldType === 'email' ? '#cffafe' :
                           fieldType === 'text' ? '#fef3c7' :
                           fieldType === 'title' ? '#e0f2fe' :
                           fieldType === 'company' ? '#ccfbf1' :
                           fieldType === 'date' ? '#fce7f3' :
                           fieldType === 'checkbox' ? '#dcfce7' :
                           fieldType === 'radio' ? '#ede9fe' :
                           fieldType === 'image' ? '#fef3c7' :
                           '#e5e7eb',
            color: fieldType === 'signature' ? '#7c3aed' :
                   fieldType === 'initials' ? '#6b21a8' :
                   fieldType === 'name' ? '#1e40af' :
                   fieldType === 'email' ? '#0e7490' :
                   fieldType === 'text' ? '#d97706' :
                   fieldType === 'title' ? '#0369a1' :
                   fieldType === 'company' ? '#0d9488' :
                   fieldType === 'date' ? '#db2777' :
                   fieldType === 'checkbox' ? '#16a34a' :
                   fieldType === 'radio' ? '#7c3aed' :
                   fieldType === 'image' ? '#ea580c' :
                   '#6b7280'
          }}>
          {fieldType}
        </span>
      </div>
      {renderInput()}
    </div>
  );
};

export default SigningSession;
